import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'

import { createPaginationWithCursorResultSchema } from '../database'

// Agreement template tags used in agreement content for signature/initial placeholders
export const AGREEMENT_TEMPLATE_TAGS = {
  SIGNATURE: '<SIGNATURE>',
  INITIAL: '<INITIAL>',
} as const

// Matches raw tags: <SIGNATURE>, <INITIAL>
export const AGREEMENT_TEMPLATE_TAGS_REGEX = new RegExp(
  `<(${Object.keys(AGREEMENT_TEMPLATE_TAGS).join('|')})>`,
  'gi'
)

// Matches HTML-encoded tags (from TinyMCE storage): &lt;SIGNATURE&gt;, &lt;INITIAL&gt;
// optionally wrapped in <span class="shortcode">...</span>
export const AGREEMENT_TEMPLATE_TAGS_ENCODED_REGEX = new RegExp(
  `(?:<span[^>]*class="shortcode"[^>]*>)?&lt;(${Object.keys(AGREEMENT_TEMPLATE_TAGS).join('|')})&gt;(?:</span>)?`,
  'gi'
)

// Count signature/initial tags in content, handling both raw and HTML-encoded forms
export const countAgreementTags = (
  content: string
): { signatureCount: number; initialCount: number; total: number } => {
  let signatureCount = 0
  let initialCount = 0

  // Try raw tags first
  const rawRegex = new RegExp(
    AGREEMENT_TEMPLATE_TAGS_REGEX.source,
    AGREEMENT_TEMPLATE_TAGS_REGEX.flags
  )
  let match
  while ((match = rawRegex.exec(content)) !== null) {
    if (match[1]!.toUpperCase() === 'SIGNATURE') signatureCount++
    else initialCount++
  }

  // If no raw tags found, try encoded tags
  if (signatureCount === 0 && initialCount === 0) {
    const encodedRegex = new RegExp(
      AGREEMENT_TEMPLATE_TAGS_ENCODED_REGEX.source,
      AGREEMENT_TEMPLATE_TAGS_ENCODED_REGEX.flags
    )
    while ((match = encodedRegex.exec(content)) !== null) {
      if (match[1]!.toUpperCase() === 'SIGNATURE') signatureCount++
      else initialCount++
    }
  }

  return { signatureCount, initialCount, total: signatureCount + initialCount }
}

// Agreement enums
export const agreementTypeEnum = z
  .enum(['SERVICE_AGREEMENT', 'POLICY', 'WAIVER'])
  .register(asEnumType, {
    name: 'AgreementType',
    description: 'Type of agreement: service agreement, policy, or waiver',
  })

export const agreementStatusEnum = z
  .enum(['PENDING', 'ACTIVE', 'EXPIRED', 'REVOKED', 'COMPLETED'])
  .register(asEnumType, {
    name: 'AgreementStatus',
    description: 'Status of client agreement',
  })

export const agreementMethodEnum = z
  .enum(['SIGNATURE', 'CHECKBOX', 'ELECTRONIC'])
  .register(asEnumType, {
    name: 'AgreementMethod',
    description: 'Method used to accept agreement',
  })

// Client Agreement Schema
export const clientAgreementSchema = z
  .object({
    agreementId: z.uuid(),
    clientId: z.uuid(),
    siteId: z.uuid(),
    siteAgreementTemplateId: z.uuid().nullable(),
    serviceId: z.uuid().nullable(),
    type: agreementTypeEnum,
    agreementVersion: z.string(),
    agreementContent: z.string(),
    agreementName: z.string(),
    signatureImageUrls: z.array(z.string()).nullable(),
    agreementMethod: agreementMethodEnum.nullable(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    signedAt: z.coerce.date().nullable(),
    expiresAt: z.coerce.date().nullable(),
    status: agreementStatusEnum,
    createdAt: z.coerce.date(),
    publicAccessLinkId: z.uuid().nullish(),
    signedViewLinkId: z.uuid().nullish(),
    signedPdfGcsUri: z.string().nullish(),
    signedPdfSha256: z.string().nullish(),
    sentByStaffUserId: z.uuid().nullish(),
    sentAt: z.coerce.date().nullish(),
    voidedAt: z.coerce.date().nullish(),
    voidedByStaffUserId: z.uuid().nullish(),
    voidReason: z.string().nullish(),
    linkedInvoiceId: z.uuid().nullish(),
  })
  .register(asObjectType, {
    name: 'ClientAgreement',
  })

// Site Agreement Template Schema
export const siteAgreementTemplateSchema = z
  .object({
    siteId: z.uuid(),
    siteAgreementTemplateId: z.uuid(),
    type: agreementTypeEnum,
    name: z.string(),
    version: z.string(),
    content: z.string(),
    templateUrl: z.string().nullable(),
    requiresSignature: z.boolean(),
    validityDays: z.number().nullable(),
    isActive: z.boolean(),
    effectiveDate: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .register(asObjectType, {
    name: 'SiteAgreementTemplate',
  })

// Create template input — picks fields from the template schema and relaxes optionality
export const createSiteAgreementTemplateInputSchema = siteAgreementTemplateSchema
  .pick({
    siteId: true,
    type: true,
    name: true,
    content: true,
    templateUrl: true,
  })
  .extend({
    name: z.string().min(1).max(200),
    version: z.string().min(1).max(20),
    templateUrl: z.string().max(500),
    requiresSignature: z.boolean().optional(),
    validityDays: z.number().int().optional(),
    effectiveDate: z.coerce.date().optional(),
  })
  .register(asObjectType, {
    name: 'CreateSiteAgreementTemplateInput',
  })

// Sign agreement input — picks identity fields from agreement schema and adds signing fields
export const signClientAgreementInputSchema = clientAgreementSchema
  .pick({
    agreementId: true,
    clientId: true,
    siteId: true,
  })
  .extend({
    signatures: z.array(z.string()).min(0),
    agreementMethod: agreementMethodEnum,
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
  })
  .register(asObjectType, {
    name: 'SignClientAgreementInput',
    description:
      'Input for signing a client agreement with base64 signature images (one per <SIGNATURE> or <INITIAL> tag)',
  })

// Create and sign agreement in one step — for inline agreement text (no template)
export const createAndSignAgreementInputSchema = z
  .object({
    clientId: z.uuid(),
    siteId: z.uuid(),
    agreementName: z.string().min(1).max(200),
    agreementContent: z.string().min(1),
    signatures: z.array(z.string()).min(0),
    agreementMethod: agreementMethodEnum,
    userAgent: z.string().optional(),
    pendingCheckout: z.boolean().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'CreateAndSignAgreementInput',
    description:
      'Input for creating and signing an agreement in one step from inline agreement text. ' +
      'Set pendingCheckout=true to keep status PENDING until checkout completes (prevents premature visibility).',
  })

// Response schema for sending agreement email
export const sendAgreementResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
  })
  .register(asObjectType, {
    name: 'SendAgreementResponse',
    description: 'Response for sending agreement notification email',
  })

// Pagination schemas
export const clientAgreementsFiltersSchema = z
  .object({
    status: z
      .object({
        value: agreementStatusEnum,
      })
      .optional(),
  })
  .optional()

export const clientAgreementsPaginationArgsSchema = z.object({
  clientId: z.string(),
  siteId: z.string().optional(),
  filters: clientAgreementsFiltersSchema,
  pageSize: z.number().optional(),
  cursor: z.string().optional(),
  search: z.string().optional(),
})

export const clientAgreementsPaginationResultSchema =
  createPaginationWithCursorResultSchema(clientAgreementSchema)

// Type exports
export type ClientAgreementSchema = z.infer<typeof clientAgreementSchema>
export type AgreementTypeSchema = z.infer<typeof agreementTypeEnum>
export type AgreementStatusSchema = z.infer<typeof agreementStatusEnum>
export type AgreementMethodSchema = z.infer<typeof agreementMethodEnum>
export type SiteAgreementTemplateSchema = z.infer<typeof siteAgreementTemplateSchema>
export type CreateSiteAgreementTemplateInputSchema = z.infer<
  typeof createSiteAgreementTemplateInputSchema
>
export type SignClientAgreementInputSchema = z.infer<typeof signClientAgreementInputSchema>
export type CreateAndSignAgreementInputSchema = z.infer<typeof createAndSignAgreementInputSchema>
export type SendAgreementResponseSchema = z.infer<typeof sendAgreementResponseSchema>
